import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    Box,
    Container,
    Flex,
    Group,
    Loader,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import { StatColumn, TextColumn } from '../../../../common';
import { useDebouncedCallback, useLocalStorage } from '@mantine/hooks';
import { useMemo, useState } from 'react';

import JsonOptionsColumn from '../../../../common/tables/columns/JsonOptionsColumn';
import { MantineReactTable } from 'mantine-react-table';
import UserRoutesTopToolbar from './toolbars/UserRoutesTopToolbar';
import classes from '../../../../../css/DetailPanelTable.module.css';
import { getUserRoutesByRouteId } from '../../../../../http/apiClient';
import { naiveRound } from '../../../../../utils/text';
import { sumObjectKeys } from '../../../../../utils/statUtils';
import { usePaginatedQuery } from '../../../../../http/queries/usePaginatedQuery';

const UserRoutesTable = ({ routeId }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) =>
            getUserRoutesByRouteId({ routeId, pagination: queryParams }),
        queryKey: ['user_routes', routeId],
        refetchInterval: 15_0000,
        pagination,
        sorting,
    });

    const { data: userRoutes, pagination: userRoutePagination } =
        paginatedData || {};

    const columns = useMemo(
        () => [
            TextColumn({
                accessorFunc: (rowData) => rowData.user?.label,
                header: 'Username',
            }),
            StatColumn('Sent', (x) => x.stats?.send),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const sends = sumObjectKeys(x.stats, ['send']);
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / sends) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const conversions = sumObjectKeys(x.stats, ['conversion']);
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((conversions / uniqueClicks) * 100, 2);
                },
                true
            ),
        ],
        [userRoutes, isLoading]
    );

    const tableData = isLoading ? [] : userRoutes;

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        //expandRowsFn: true, // Assuming this should be true, since it was provided without a value
        columns: columns,
        data: tableData,
        enableSorting: false,
        enableColumnActions: false,
        enableDensityToggle: false,

        editDisplayMode: 'row',
        createDisplayMode: 'row',
        enableHiding: false,
        enableFilters: false,
        enableEditing: false,
        enableResizing: true,
        enableFullScreenToggle: false,
        defaultColumn: {
            minSize: 0,
            maxSize: Number.MAX_SAFE_INTEGER,
        },

        /*onCreatingRowChange: handleNewCreate,
        onCreatingRowSave: handleCreateSave,
        onCreatingRowCancel: handleCreateCancel,
        onEditingRowSave: handleEditSave,
        onEditingRowCancel: handleEditCancel,*/

        //state: { rowSelection },
        mantineTopToolbarProps: () => ({
            className: classes.toolbar,
        }),
        enableTopToolbar: true,
        renderTopToolbarCustomActions: ({ props: table }) => (
            <UserRoutesTopToolbar table={table} />
        ),
        enableBottomToolbar: false,
        enableRowActions: false,
        positionActionsColumn: 'last',
        positionExpandColumn: 'last',
        mantineTableContainerProps: {
            style: {
                minHeight: '200px',
            },
        },

        mantineTableBodyRowProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => ({
            className: isDetailPanel ? classes.rowPadding : null,
            onClick: (e) => {
                const INTERACTIVE_SELECTOR =
                    'a[href], button, input:not([type="hidden"]), select, textarea, label, [role="button"]';
                const interactiveElemnt =
                    e.target instanceof HTMLElement &&
                    e.target.closest(INTERACTIVE_SELECTOR);
                if (interactiveElemnt) return;

                if (!isDetailPanel) {
                    row.toggleExpanded();
                }
            },
        }),

        /*renderRowActions: ({ table, row }) => ({
            /*<FilterActionsCell
                table={table}
                row={row}
                form={form}
                setTableData={setTableData}
                setRowSelection={setRowSelection}
            /> *
        }),*/
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,

        rowCount: userRoutePagination?.totalItems || 0,
        pageCount: userRoutePagination?.totalPages || 1,
        initialState: {
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
        state: {
            density: 'xs',
        },
        meta: {
            dataLength: tableData.length,
            originalLength: userRoutes?.length || 0,
        },
    };

    if (isLoading) {
        return (
            <Group
                w={'100%'}
                mih={100}
                justify={'center'}>
                <Loader />
            </Group>
        );
    }

    return (
        <>
            <MantineReactTable {...tableProps} />
        </>
    );
};
export default UserRoutesTable;
